import { pxToRem } from '@fluentsms/agentnet-web-components';
import { HourglassEmptyOutlined } from '@material-ui/icons';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import BlockIcon from '@material-ui/icons/Block';
import CancelIcon from '@material-ui/icons/Cancel';
import CancelOutlinedIcon from '@material-ui/icons/CancelOutlined';
import ErrorIcon from '@material-ui/icons/Error';
import ErrorOutlineOutlinedIcon from '@material-ui/icons/ErrorOutlineOutlined';
import Help from '@material-ui/icons/Help';
import HelpOutlineOutlinedIcon from '@material-ui/icons/HelpOutlineOutlined';
import ReportProblemIcon from '@material-ui/icons/ReportProblem';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import VerifiedUserOutlinedIcon from '@material-ui/icons/VerifiedUserOutlined';
import { createStyles, makeStyles } from '@material-ui/styles';
import clsx from 'clsx';
import { IntegrationInstructionsOutlined } from 'core/navbar/LeftNavIcons';
import React from 'react';
import HourglassTopOutlined from 'ui-kit/icons/HourglassTopOutlined';
import { theme } from 'ui-kit/theme/theme';

export enum RiskSeverity {
  Success = 'Success',
  Alert = 'Alert',
  Warning = 'Warning',
  None = 'None',
  Error = 'Error',
  Instruction = 'Instruction',
  Loading = 'Loading',
  Pending = 'Pending',
  Expired = 'Expired',
  LimitReached = 'Limit Reached',
}

// Define the prop type
type OrderInsightsRiskIconProps = {
  riskSeverity?: string;
  isPdf?: boolean;
  isPartyChip?: boolean;
};
const useStyles = makeStyles(() =>
  createStyles({
    orderInsightFontSize: {
      fontSize: pxToRem(24),
    },
    pdfFontSize: {
      fontSize: pxToRem(12),
    },
    green: {
      color: '#3B873E',
    },
    orange: {
      color: '#C77700',
    },
    red: {
      color: '#B3150A',
    },
    grey: {
      color: theme.palette.text.secondary,
    },
    center: {
      display: 'flex',
      alignItems: 'center',
    },
    instructionIcon: {
      '& svg': {
        width: '24px',
        height: '24px',
      },
    },
    limitReached: {
      color: theme.palette.accordion.seller,
    },
    partyChips: {
      marginRight: pxToRem(-4),
    },
  }),
);

const OrderInsightsRiskIcon: React.FC<OrderInsightsRiskIconProps> = ({ riskSeverity, isPdf, isPartyChip }) => {
  const classes = useStyles();

  return (
    <>
      {isPdf ? (
        <div className={clsx(classes.center, 'iconContainer')}>
          {riskSeverity === RiskSeverity.Success && (
            <VerifiedUserIcon className={clsx(classes.pdfFontSize, classes.green)} />
          )}
          {riskSeverity === RiskSeverity.Alert && <ErrorIcon className={clsx(classes.pdfFontSize, classes.red)} />}
          {riskSeverity === RiskSeverity.Warning && (
            <ReportProblemIcon className={clsx(classes.pdfFontSize, classes.orange)} />
          )}
          {riskSeverity === RiskSeverity.None && <Help className={clsx(classes.pdfFontSize, classes.grey)} />}
          {riskSeverity === RiskSeverity.Error && <CancelIcon className={clsx(classes.pdfFontSize, classes.grey)} />}
          {riskSeverity === RiskSeverity.Pending && (
            <HourglassTopOutlined
              isPdf
              className={clsx(classes.pdfFontSize, classes.grey, 'oi-icon-error')}
              fill={theme.palette.status.pending}
            />
          )}
        </div>
      ) : (
        <div className={clsx(classes.center, classes.pdfFontSize, 'iconContainer', isPartyChip && classes.partyChips)}>
          {riskSeverity === RiskSeverity.Success && (
            <VerifiedUserOutlinedIcon className={clsx(classes.orderInsightFontSize, classes.green)} />
          )}
          {riskSeverity === RiskSeverity.Alert && (
            <ErrorOutlineOutlinedIcon
              htmlColor="#B3150A"
              className={clsx(classes.orderInsightFontSize, 'oi-icon-alert')}
            />
          )}
          {riskSeverity === RiskSeverity.Warning && (
            <ReportProblemOutlinedIcon
              className={clsx(classes.orderInsightFontSize, classes.orange, 'oi-icon-warning')}
            />
          )}
          {riskSeverity === RiskSeverity.None && (
            <HelpOutlineOutlinedIcon className={clsx(classes.orderInsightFontSize, classes.grey, 'oi-icon-none')} />
          )}
          {riskSeverity === RiskSeverity.Error && (
            <CancelOutlinedIcon className={clsx(classes.orderInsightFontSize, classes.grey, 'oi-icon-error')} />
          )}
          {riskSeverity === RiskSeverity.Instruction && (
            <div className={clsx(classes.orderInsightFontSize, classes.instructionIcon)}>
              <IntegrationInstructionsOutlined />
            </div>
          )}
          {riskSeverity === RiskSeverity.Loading && (
            <HourglassEmptyOutlined className={clsx(classes.orderInsightFontSize, classes.grey, 'oi-icon-error')} />
          )}
          {riskSeverity === RiskSeverity.Pending && (
            <HourglassTopOutlined
              className={clsx(classes.orderInsightFontSize, classes.grey, 'oi-icon-error')}
              fill={isPartyChip ? theme.palette.status.waiting : theme.palette.status.pending}
              size={isPartyChip ? 16 : 24}
            />
          )}
          {riskSeverity === RiskSeverity.Expired && (
            <AccessTimeIcon className={clsx(classes.orderInsightFontSize, classes.grey, 'oi-icon-error')} />
          )}
          {riskSeverity === RiskSeverity.LimitReached && (
            <BlockIcon className={clsx(classes.orderInsightFontSize, classes.limitReached, 'oi-icon-error')} />
          )}
        </div>
      )}
    </>
  );
};

export default OrderInsightsRiskIcon;
